import type { CommandCenterDataQualityRow } from "../lib/commandCenterViewModel";

interface CommandCenterDataQualityPanelProps {
  rows: CommandCenterDataQualityRow[];
}

const S = {
  wrap: {
    background: "rgba(12,12,24,0.85)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "8px",
    padding: "10px",
    marginBottom: "12px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "6px",
    marginBottom: "6px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  title: {
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    color: "#a0a0c0",
    textTransform: "uppercase" as const,
  },
  meta: {
    marginLeft: "auto",
    fontSize: "9px",
    color: "#707090",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1.25fr 72px 1fr 72px",
    alignItems: "center",
    gap: "6px",
    padding: "4px 0",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  headCell: {
    fontSize: "8px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: "#707090",
    textTransform: "uppercase" as const,
  },
  cell: {
    fontSize: "9px",
    color: "#c0c0d0",
    minWidth: 0,
  },
  note: {
    marginTop: "6px",
    fontSize: "9px",
    color: "#707090",
  },
};

function severityColor(severity: CommandCenterDataQualityRow["severity"]): string {
  if (severity === "ok") return "#22c55e";
  if (severity === "watch") return "#f59e0b";
  return "#ef4444";
}

function missingText(symbols: string[]): string {
  return symbols.length ? symbols.join(", ") : "none";
}

export function CommandCenterDataQualityPanel({ rows }: CommandCenterDataQualityPanelProps) {
  return (
    <section style={S.wrap} aria-label="Command Center data quality">
      <div style={S.header}>
        <span style={S.title}>Command Center Data Quality</span>
        <span style={S.meta}>critical field coverage</span>
      </div>
      <div role="table" aria-label="Command Center critical field coverage">
        <div style={S.row} role="row">
          <span style={S.headCell}>Panel</span>
          <span style={S.headCell}>Field group</span>
          <span style={S.headCell}>Coverage</span>
          <span style={S.headCell}>Missing symbols</span>
          <span style={S.headCell}>Severity</span>
        </div>
        {rows.map((row) => (
          <div style={S.row} role="row" key={`${row.panel}:${row.fieldGroup}`}>
            <span style={{ ...S.cell, fontWeight: 800, color: "#e0e0f0" }}>{row.panel}</span>
            <span style={S.cell}>{row.fieldGroup}</span>
            <span style={S.cell}>{row.present}/{row.expected}</span>
            <span style={S.cell}>{missingText(row.missingSymbols)}</span>
            <span style={{ ...S.cell, color: severityColor(row.severity), fontWeight: 800 }}>{row.severity}</span>
          </div>
        ))}
      </div>
      <div style={S.note}>Audit surface only. Missing fields must be investigated before treating blank/neutral cells as healthy data.</div>
    </section>
  );
}
